import { NavLink, Outlet, useNavigate, useLocation, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutDashboard, Calculator, FileText, Wrench, Users, Package, CalendarClock,
  Gauge, BarChart3, MapPinned, Store, CreditCard, Settings, ShieldCheck, LogOut, Menu, Boxes, Headset, Handshake, GraduationCap, BookOpen, Wallet, MessageCircle, UserCog, ClipboardList,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { fmtDate } from "@/lib/format";

const NAV = [
  { to: "/app", end: true, label: "Dashboard", icon: LayoutDashboard },
  { to: "/app/calculadora", label: "Calculadora", icon: Calculator },
  { to: "/app/custos", label: "Meus Custos", icon: Wallet, beta: true },
  { to: "/app/contratos", label: "Contratos", icon: ClipboardList, beta: true },
  { to: "/app/orcamentos", label: "Orçamentos", icon: FileText },
  { to: "/app/os", label: "Ordens de Serviço", icon: Wrench },
  { to: "/app/clientes", label: "Clientes", icon: Users },
  { to: "/app/equipe", label: "Equipe", icon: UserCog },
  { to: "/app/servicos", label: "Serviços", icon: Package },
  { to: "/app/pecas", label: "Peças", icon: Package },
  { to: "/app/materiais", label: "Materiais", icon: Boxes, beta: true },
  { to: "/app/assist", label: "GEPR Assist", icon: Headset, beta: true },
  { to: "/app/network", label: "GEPR Network", icon: Handshake, beta: true },
  { to: "/app/mentor", label: "GEPR Mentor", icon: GraduationCap, beta: true },
  { to: "/app/partner", label: "GEPR Partner", icon: BookOpen, beta: true },
  { to: "/app/manutencao", label: "Manutenção", icon: CalendarClock },
  { to: "/app/ferramentas", label: "Ferramentas", icon: Gauge },
  { to: "/app/relatorios", label: "Relatórios", icon: BarChart3 },
  { to: "/app/mercado", label: "Preços Regionais", icon: MapPinned, beta: true },
  { to: "/app/diretorio", label: "Meu Diretório", icon: Store },
  { to: "/app/planos", label: "Planos", icon: CreditCard },
  { to: "/app/configuracoes", label: "Configurações", icon: Settings },
];

function NavItems({ onClick }) {
  const { user } = useAuth();
  const role = user?.member_role;
  const HIDE = role === "tecnico"
    ? ["/app", "/app/custos", "/app/contratos", "/app/relatorios", "/app/planos", "/app/configuracoes", "/app/equipe", "/app/assist", "/app/network", "/app/mentor", "/app/partner", "/app/materiais", "/app/diretorio"]
    : role === "gerente" ? ["/app/planos", "/app/equipe"] : [];
  return (
    <nav className="flex flex-col gap-1">
      {NAV.filter((n) => !HIDE.includes(n.to)).map(({ to, end, label, icon: Icon, beta }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={onClick}
          data-testid={`nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
              isActive ? "bg-sky-600 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"
            }`
          }
        >
          <Icon className="h-4.5 w-4.5 h-5 w-5 shrink-0" />
          <span className="flex-1">{label}</span>
          {beta && <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-[10px]">BETA</Badge>}
        </NavLink>
      ))}
      {user?.role === "admin" && (
        <NavLink
          to="/app/admin"
          onClick={onClick}
          data-testid="nav-admin"
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
              isActive ? "bg-sky-600 text-white" : "text-amber-400 hover:bg-slate-800"
            }`
          }
        >
          <ShieldCheck className="h-5 w-5 shrink-0" />
          Administração
        </NavLink>
      )}
    </nav>
  );
}

export default function AppLayout() {
  const { user, company, plan, planId, trialActive, trialEndsAt, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [waSupport, setWaSupport] = useState("");

  useEffect(() => {
    api.get("/platform-config").then((r) => setWaSupport(r.data?.support_whatsapp || "")).catch(() => {});
  }, []);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  if (user?.member_role === "tecnico" && location.pathname === "/app") {
    return <Navigate to="/app/os" replace />;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 bg-slate-900/95 backdrop-blur-md text-white border-b border-slate-800 shadow-lg">
        <div className="px-4 sm:px-6 h-14 flex items-center justify-between max-w-[1600px] mx-auto">
          <div className="flex items-center gap-3">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden text-white hover:bg-slate-800 hover:text-white h-11 w-11" data-testid="mobile-menu-btn">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="bg-slate-900 border-slate-800 text-white w-72 p-4">
                <Logo light />
                <div className="mt-6">
                  <NavItems />
                </div>
              </SheetContent>
            </Sheet>
            <NavLink to="/app">
              <Logo light />
            </NavLink>
          </div>
          <div className="flex items-center gap-3">
            <Badge className="hidden sm:inline-flex bg-sky-600/20 text-sky-300 border-sky-500/30" data-testid="plan-badge">
              {trialActive ? `Teste Profissional até ${fmtDate(trialEndsAt)}` : `Plano ${plan.name || planId}`}
            </Badge>
            <span className="hidden md:block text-sm text-slate-300" data-testid="company-name">{company.name || user?.name}</span>
            <Button variant="ghost" size="icon" onClick={doLogout} className="text-slate-300 hover:bg-slate-800 hover:text-white h-11 w-11" data-testid="logout-btn" title="Sair">
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      {user?.member_role && (
        <div className="bg-amber-50 border-b border-amber-200 text-amber-800 text-xs px-4 py-1.5 text-center" data-testid="member-banner">
          Você está na conta de <b>{user.member_of || "seu responsável"}</b> como {user.member_role === "gerente" ? "Gerente" : "Técnico"}.
        </div>
      )}

      <div className="flex max-w-[1600px] mx-auto">
        <aside className="hidden lg:block w-64 shrink-0 bg-slate-900 min-h-[calc(100vh-3.5rem)] p-4 sticky top-14 self-start">
          <NavItems />
        </aside>
        <main className="flex-1 p-4 sm:p-6 pb-24 lg:pb-8 min-w-0">
          <Outlet />
        </main>
      </div>

      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-slate-900 border-t border-slate-800 grid grid-cols-5 h-16">
        {[
          { to: "/app", label: "Início", icon: LayoutDashboard, end: true },
          { to: "/app/calculadora", label: "Calcular", icon: Calculator },
          { to: "/app/orcamentos", label: "Orçamentos", icon: FileText },
          { to: "/app/clientes", label: "Clientes", icon: Users },
          { to: "/app/os", label: "OS", icon: Wrench },
        ].map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            data-testid={`bottomnav-${label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-0.5 text-[11px] font-medium ${
                isActive ? "text-sky-400" : "text-slate-400"
              }`
            }
          >
            <Icon className="h-5 w-5" />
            {label}
          </NavLink>
        ))}
      </nav>

      {waSupport && (
        <a href={`https://wa.me/${waSupport.replace(/\D/g, "")}?text=${encodeURIComponent("Olá! Preciso de suporte com o GEPR.")}`}
           target="_blank" rel="noreferrer"
           className="fixed bottom-20 lg:bottom-6 right-4 lg:right-6 z-50 h-14 w-14 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white flex items-center justify-center shadow-lg transition-transform hover:scale-105"
           data-testid="whatsapp-support-btn" title="Suporte via WhatsApp">
          <MessageCircle className="h-7 w-7" />
        </a>
      )}
    </div>
  );
}
